/**
 * Product-oriented tuning philosophy content, aligned with in-app Basic Stats workflow
 * (see knowledge base / tuning_philosophy_basic_stats.md).
 */

export const philosophyIntro = {
  title: 'What this guide is',
  body:
    'ShokIQ sits between a gimmick and a lab rig: enough depth to tune with confidence, enough clarity to act after every ride. This is our complete in-app reference for reading fork and shock telemetry and turning it into on-bike decisions—aligned with Basic Stats, compare runs, and the summary bands you can set under Settings → Advanced (Summary tuning guide).',
};

/** Public page: teach concepts, stay lighter on product; full depth in-app. */
export const philosophyIntroPublic = {
  title: 'How we think about suspension data',
  body:
    'Good tuning blends on-bike feel with a clear picture of how the fork and shock are working over a run—depth in the stroke, balance between ends, and how hard hits are demanding damping. This page shares the mental model we use when we read telemetry. It is deliberately not exhaustive: thresholds, glossary detail, and portal workflow live in the in-app Tuning guide for signed-in riders.',
};

/** Public FAQ: education-first; avoids full numeric tables reserved for Tuning guide. */
export const philosophyFaqCategoriesPublic = [
  {
    id: 'public-measure',
    title: 'You cannot tune what you do not measure',
    blurb: 'Why vague memory and “two clicks out” are fragile when you are chasing real changes.',
    items: [
      {
        id: 'why-measure',
        q: 'Why insist on measured metrics?',
        a: [
          'Every spring and damping change should answer a question: did travel usage, balance, velocities, or events move in the direction we expected?',
          'Without a record, those answers default to vibes—which is fine for a quick trail-side tweak, but weak when you are swapping tokens, air pressure, or clickers across multiple sessions.',
        ],
      },
      {
        id: 'dynamic-beyond-static',
        q: 'What does on-trail behavior add beyond static setup?',
        a: [
          'Static checks (sag, o-ring position) are necessary but incomplete: they do not show how travel is used under braking, cornering, and repeated hits.',
          'Seeing shaft behavior over time answers different questions—whether the suspension returns between bumps, which end is doing more work, and whether damping demand looks sane for the terrain.',
        ],
      },
    ],
  },
  {
    id: 'public-definitions',
    title: 'Big ideas: strokes, travel, compression, rebound',
    blurb: 'Shared vocabulary—useful on its own and before you read any chart.',
    items: [
      {
        id: 'what-stroke',
        q: 'What is a “stroke” in this context?',
        a: [
          'A meaningful compression of the fork or shock shaft into the travel, followed by a return (extension) phase. A busy trail produces many strokes per run.',
          'Summaries (counts, depths, velocities, bottom-outs) roll lots of strokes into a few numbers so you can compare laps, bikes, or setups without hand-scoring every bump.',
        ],
      },
      {
        id: 'travel-sag',
        q: 'Travel vs sag—what is the difference?',
        a: [
          'Travel is the total usable shaft range. Sag is where the suspension sits under rider weight (and how you set spring pressure or preload).',
          'On the bike, what matters is dynamic behavior: how deep you live in the stroke on average, how often you touch the ends, and how the bike pitches—not a single parked sag number in isolation.',
        ],
      },
      {
        id: 'compression-rebound-plain',
        q: 'What is compression? What is rebound?',
        a: [
          'Compression: the shaft moves deeper into the travel as the wheel hits a bump or the chassis loads the suspension. Rebound: the shaft extends again as the spring and damper release that energy.',
          'Dampers add controlled resistance in both directions. “Low speed” adjusters mostly affect body movements, berms, and rolling terrain; “high speed” adjusters matter more on sharp, fast shaft motion.',
        ],
      },
      {
        id: 'velocity-plain',
        q: 'Why do shaft velocities matter?',
        a: [
          'Velocity is how fast the shaft is moving. Damping force scales with speed, so compression and rebound velocities hint at whether low- or high-speed damping circuits are doing the heavy lifting.',
          'You do not need a physics background: treat them as clues alongside travel and balance—structured summaries exist so those clues stay readable.',
        ],
      },
    ],
  },
  {
    id: 'public-symptoms-lite',
    title: 'Problems, directions, sensible next steps',
    blurb: 'Symptom → what to look at—not a full decision tree; the Tuning guide goes deeper.',
    items: [
      {
        id: 'bottom-lite',
        q: 'Frequent hard bottom-outs',
        a: [
          'Problem: not enough support at the end of travel. Direction: spring rate or progression first (air pressure, tokens, coil rate)—adding compression damping alone can mask metal-on-metal fatigue while travel use stays wrong.',
        ],
      },
      {
        id: 'pack-lite',
        q: 'Bike feels dead or “stuck down”',
        a: [
          'Problem: the suspension may not be returning between hits (packing) or spring balance is off. Direction: rebound setup and spring/sag split front vs rear; confirm whether travel actually recovers on small rollers between bigger hits.',
        ],
      },
      {
        id: 'imbalance-lite',
        q: 'Front vs rear feels disconnected',
        a: [
          'Problem: one end may be absorbing disproportionate work. Direction: match spring rates and sag front vs rear before chasing damping at one end to compensate for the other end’s spring.',
        ],
      },
    ],
  },
  {
    id: 'public-get-shokiq',
    title: 'Want to go further?',
    blurb: 'Beyond this overview you need the device and the platform.',
    items: [
      {
        id: 'need-device',
        q: 'Where does the rest of the detail live?',
        a: [
          'On ShokIQ: ride with the hardware, upload to our platform, and you get the longer tuning guidance—how to work through specific problems—with your own data next to it.',
        ],
      },
    ],
  },
];

export const philosophyFaqCategories = [
  {
    id: 'value',
    title: 'Why measured metrics (and bands) beat guesswork',
    blurb: 'What you gain when every run produces comparable summaries—not just seat-of-pants recall.',
    items: [
      {
        id: 'within-range-value',
        q: 'What is the practical value of “within range”?',
        a: [
          'Bands turn long tables into decisions: green means move on; yellow means verify against feel; red means pick one lever (spring, sag, LSC, HSC, LSR, HSR) with intent.',
          'They also make A/B tests honest—same trail, two setups, did the metrics move the way the change predicted?',
        ],
      },
      {
        id: 'cost-guesswork',
        q: 'Where is the cost of not measuring?',
        a: [
          'Parts and shop time spent chasing the wrong adjuster, rides “lost” to setup roulette, and confidence erosion when two people disagree on what changed.',
          'Measured runs amortize that cost: you pay once for hardware and logging discipline, then every upload compounds what you know about your bike.',
        ],
      },
      {
        id: 'financial-framing',
        q: 'How should I think about ROI on a logger + portal?',
        a: [
          'Compare the price of one unnecessary cartridge swap or spring kit against seasons of structured evidence about what your suspension is doing.',
          'Even a handful of avoided wrong turns usually exceeds the time cost of reviewing Basic Stats after key rides.',
        ],
      },
    ],
  },
  {
    id: 'portal',
    title: 'Using this guide inside ShokIQ',
    blurb: 'How Basic Stats, compares, and settings line up with the FAQ sections below.',
    items: [
      {
        id: 'read-order',
        q: 'Suggested review order after a ride',
        a: [
          'Start with position: dynamic sag, max travel, bottom-outs, top-out behavior—this frames spring and balance.',
          'Then stroke depths and velocities for damping demand; then stroke counts for packing or imbalance.',
          'Finish with the symptom map when you already have a hypothesis you want to sanity-check.',
        ],
      },
      {
        id: 'bands-settings',
        q: 'Where do the green/yellow/red bands come from?',
        a: [
          'Configure summary bands under Settings → Advanced (Summary tuning guide). They are stored per browser and should reflect your bike, terrain, and acceptable bottom-out rate.',
          'Tight bands surface more yellow/red; loose bands are quieter—tune the bands as you learn what “healthy” looks like for you.',
        ],
      },
      {
        id: 'compare-runs',
        q: 'Why compare two runs side by side?',
        a: [
          'Same rider, same trail section, different setup: compare is the fastest way to prove a change did what you expected.',
          'When trail conditions differ, compare cautiously—use feel plus absolute bands, not only delta vs another noisy day.',
        ],
      },
    ],
  },
  {
    id: 'definitions',
    title: 'Terms and definitions',
    blurb: 'Glossary for the rest of this guide and Basic Stats labels.',
    items: [
      {
        id: 'comp-vs-reb',
        q: 'Compression vs rebound—what do we mean?',
        a: [
          'Compression phase: shaft moves deeper into travel (fork/shock compresses). Rebound phase: shaft extends again between hits.',
          'Low-speed compression (LSC) and low-speed rebound (LSR) mostly affect shaft speeds typical of weight shifts, berms, and rolling terrain. High-speed compression (HSC) and high-speed rebound (HSR) matter more on sharp square-edge hits and rapid shaft motion.',
        ],
      },
      {
        id: 'percentile',
        q: 'What is a percentile (p90, p95)?',
        a: [
          'Take one number measured on every stroke (e.g. peak rebound velocity for that stroke). Sort all strokes from low to high on that metric.',
          'The p95 is the value where 95% of strokes are at or below it and 5% are harder—that cuts single crazy outliers so you tune to “typical hard” behavior. p90 is the same idea at 90%; we cite p90 depth when talking about HSC-style depth use.',
        ],
      },
      {
        id: 'peak-vs-p95-def',
        q: 'Peak vs p95 on a velocity readout',
        a: [
          'Peak is the single largest value in the run—easy to chase, easy to misread if one spike was a weird event.',
          'p95 is the robust headline for “how hard the damper is usually asked to work” on rebound and compression velocity reads in this guide.',
        ],
      },
      {
        id: 'avg-peak-vel-def',
        q: 'What is “average peak” velocity?',
        a: [
          'For each stroke, take the peak shaft speed during that stroke; average peak is the mean of those peaks—think “typical hit intensity,” not the one biggest spike.',
        ],
      },
      {
        id: 'stroke-count-depth-def',
        q: 'Stroke count vs stroke depth',
        a: [
          'Stroke count: how many meaningful compression cycles occurred—useful for front/rear balance and packing clues.',
          'Stroke depth: how deep into travel strokes go (mean, p90, etc.)—ties together spring, sag, and compression damping.',
        ],
      },
      {
        id: 'dynamic-sag-def',
        q: 'Dynamic sag vs static sag',
        a: [
          'Static sag: bike parked, rider position, tape or sag ring. Dynamic sag in the portal: mean shaft position % while riding—includes trail shape, braking, and repeated hits.',
        ],
      },
      {
        id: 'bottom-top-def',
        q: 'Bottom-out vs top-out stats',
        a: [
          'Bottom-out: meaningful use of full compression—interpret with terrain (XC vs DH) and consequence.',
          'Top-out % / count: time or events near full extension between strokes—pairs with rebound and packing reads.',
        ],
      },
    ],
  },
  {
    id: 'position',
    title: 'Position, sag, and stroke extremes',
    blurb: 'Start here on every review: sag, bottom-outs, and top-out behavior.',
    items: [
      {
        id: 'dynamic-sag',
        q: 'What should I check first in position stats?',
        a: [
          'Confirm dynamic sag (mean position) is close to your target (for example ~30% of travel).',
          'Compare front vs rear sag: a large mismatch means the bike is pitching nose-up or nose-down under load, which skews handling before you touch clickers.',
        ],
      },
      {
        id: 'bottom-outs',
        q: 'How do I interpret max travel and bottom-outs?',
        a: [
          'Use them together. “Acceptable” bottom-outs depend on consequence: zero may be right on technical terrain; a couple per run can be fine on rough enduro or DH.',
          'Too many bottom-outs for the trail → consider more spring rate or progression (tokens / air chamber).',
          'Low max travel with zero bottom-outs → spring may be too stiff or sag too high; suspension is not being used.',
        ],
      },
      {
        id: 'top-out',
        q: 'What does top-out time % and top-out count tell me?',
        a: [
          'High top-out % often means rebound too fast (shaft extends past the natural position between hits) or spring too stiff (wheel pushed to full extension).',
          'High top-out can also mean the bike is not packing—packed suspension shows lower top-out because it never fully extends between hits.',
          'Front top-out count is usually higher than rear; if rear ≥ front, balance is off (rear spring soft or front rebound too fast relative to rear)—use other metrics to pinpoint.',
        ],
      },
    ],
  },
  {
    id: 'velocity',
    title: 'Stroke velocities (compression & rebound)',
    blurb: 'Per-stroke peak velocities reflect damping demand—not raw noise.',
    items: [
      {
        id: 'p95',
        q: 'Why is the 95th percentile velocity the main reference?',
        a: [
          'Rebound p95 around 1000–1400 mm/s is a practical high-speed rebound (HSR) reference band for many setups.',
          'Compression p95 is trail-dependent (often ~2500–4000 mm/s); use it as supporting evidence, not the only HSC target.',
          'For high-speed compression (HSC), prefer 90th percentile stroke depth (see stroke depth section).',
        ],
      },
      {
        id: 'avg-peak',
        q: 'What does average peak velocity tell me?',
        a: [
          'It reflects typical stroke demand—mapped to low-speed rebound (LSR) and low-speed compression (LSC).',
          'High average vs typical feel → LSR/LSC may be too soft; low average with a harsh ride → may be over-damped.',
          'Compare average to p95: close together suggests a progressive damping curve; avg well below p95 suggests more low-speed work (digressive/linear spread).',
        ],
      },
      {
        id: 'peak-vs-p95',
        q: 'Should I tune to peak velocity?',
        a: [
          'No—tune to p95, not peak. Peak captures rare outliers.',
          'If peak ≈ p95, hardest hits are not far above the main band. If peak >> p95, a few extreme hits exist; still anchor decisions on p95.',
        ],
      },
      {
        id: 'rebound-vs-comp',
        q: 'How should rebound and compression velocities relate?',
        a: [
          'Rebound velocity should generally be lower than compression: the wheel compresses hard into hits and returns under control.',
          'If rebound ≥ compression, rebound is likely too fast and the wheel can kick.',
        ],
      },
    ],
  },
  {
    id: 'depth',
    title: 'Stroke depth',
    blurb: 'Average depth and p90 depth connect spring, sag, and HSC behavior.',
    items: [
      {
        id: 'avg-depth',
        q: 'What does average stroke depth indicate?',
        a: [
          'It combines spring rate, sag, and low-speed damping: shallow can mean stiff spring, heavy LSC, or slow rebound (packing); deep can mean soft spring, low sag, or open LSC.',
          'Cross-check with sag to separate spring issues from damping issues.',
        ],
      },
      {
        id: 'p90-hsc',
        q: 'How do I use p90 compression stroke depth for HSC?',
        a: [
          'p90 depth is the depth that 90% of compression strokes fall below—useful for HSC evaluation.',
          'If p90 stays deep, compression may not resist big inputs enough → consider more HSC.',
          'Low p90 with healthy average depth can mean HSC is doing its job.',
          'Compare before/after datasets to verify an HSC change did what you expected.',
        ],
      },
      {
        id: 'f-r-depth',
        q: 'Front vs rear depth balance',
        a: [
          'The rear wheel sees more trail input, so rear average depth is normally slightly higher than the fork.',
          'Fork much deeper than shock → front working harder per hit: fork spring, LSC, or sag.',
          'Shock much deeper than expected → rear overloaded: rear spring, sag, or LSC.',
        ],
      },
    ],
  },
  {
    id: 'counts',
    title: 'Stroke counts',
    blurb: 'Balance checks across ends and stroke directions.',
    items: [
      {
        id: 'fr-count',
        q: 'What do front vs rear stroke counts show?',
        a: [
          'Counts should be in the same ballpark. One end far ahead is absorbing disproportionate input—spring, sag, or geometry balance.',
        ],
      },
      {
        id: 'comp-reb-count',
        q: 'What if compression count exceeds rebound?',
        a: [
          'Large gaps (many more compressions than rebounds) can indicate packing—the suspension is not returning between hits. Consider rebound changes.',
        ],
      },
      {
        id: 'd1-d2',
        q: 'Why compare stroke counts between two datasets?',
        a: [
          'On the same trail section, a big drop in count after a tune change can reflect packing or a setup that suppresses strokes—sanity-check against feel.',
        ],
      },
    ],
  },
  {
    id: 'symptoms',
    title: 'Symptoms → actions (quick map)',
    blurb: 'Use with the full table in the app workflow; these are the high-signal shortcuts.',
    items: [
      {
        id: 'bottom-too-many',
        q: 'Too many bottom-outs for the terrain',
        a: ['Increase spring rate or add progression (air volume / tokens).'],
      },
      {
        id: 'no-use-travel',
        q: 'Little max travel, no bottom-outs',
        a: ['Spring may be too stiff or sag too high—reduce rate or adjust sag.'],
      },
      {
        id: 'topout-high',
        q: 'High top-out percentage',
        a: ['Slow rebound (LSR/HSR) or address spring if sag is also wrong.'],
      },
      {
        id: 'packing',
        q: 'Signs of packing in the numbers',
        a: [
          'Low top-out % with sag climbing, or comp stroke count >> reb count → rebound may be too slow; open rebound.',
        ],
      },
      {
        id: 'reb-p95',
        q: 'Rebound p95 outside ~1000–1400 mm/s',
        a: [
          'Above band → HSR likely under-damped for that trail (add HSR).',
          'Below band → HSR heavy or spring too stiff (remove HSR, verify spring).',
        ],
      },
      {
        id: 'reb-vs-comp-vel',
        q: 'Rebound velocity ≥ compression velocity',
        a: ['Rebound too fast—add LSR and HSR in sensible steps.'],
      },
      {
        id: 'harsh-chatter',
        q: 'Harsh on small chatter but big hits feel OK',
        a: [
          'Often LSC or spring too stiff for high-frequency inputs, or rebound slightly slow causing micro-packing—check average depth vs sag and rebound p95 before adding spring.',
        ],
      },
      {
        id: 'fr-imbalance-symptom',
        q: 'Numbers show one end doing most of the work',
        a: [
          'Treat as balance first: spring rate and sag front vs rear, then LSC split. Do not max one end’s rebound to compensate for the other end’s spring.',
        ],
      },
      {
        id: 'inconsistent-days',
        q: 'Metrics swing wildly between “similar” days',
        a: [
          'Check tire pressure, temperature, and whether the trail was wet; then verify sensor zero and file completeness.',
          'If raw ride quality truly differed, expect velocity and depth variance—use compare on the closest-matched segments only.',
        ],
      },
    ],
  },
];
